<template>
  <div class="program-page-preview" :class="{ 'program-page-preview--map': isMapPageType(type) }">
    <div v-if="!hasHtml" class="program-page-preview__empty caption grey--text">
      Start writing content in the Editor tab to see the preview.
    </div>

    <template v-else>
      <div
        v-for="(pageHtml, index) in previewPages"
        :key="`preview-page-${index}`"
        class="program-page-preview__page-wrap"
      >
        <div class="program-page-preview__sheet">
          <div class="program-page-preview__header-placeholder program-page-preview__logo-slot">
            <div class="program-page-preview__logo-mark"></div>
          </div>
          <div v-if="!isMapPageType(type) && index === 0" class="program-page-preview__title-block">
            <div v-if="titleParts.eyebrow" class="program-page-preview__eyebrow">{{ titleParts.eyebrow }}</div>
            <h1 class="program-page-preview__title">{{ titleParts.mainTitle }}</h1>
            <div class="program-page-preview__title-rule"></div>
          </div>
          <div class="program-page-preview__html" v-html="pageHtml"></div>
        </div>
      </div>
    </template>

    <div ref="measureHost" class="program-page-preview__measure" aria-hidden="true"></div>
  </div>
</template>

<script>
import { normalizePageContent, isMapPageType } from "../utils";
import { packProgramPageHtml, watchImagesForRepack } from "../programPagePacking";

export default {
  props: {
    title: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "excursions_visites",
    },
    content: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      previewPages: [],
      packQueued: false,
      packTimer: null,
      imagePackPasses: 0,
      unwatchImages: null,
    };
  },
  computed: {
    displayTitle() {
      return String(this.title || "").trim() || "Untitled page";
    },
    titleParts() {
      const lines = String(this.title || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) {
        return { eyebrow: "", mainTitle: "Untitled page" };
      }
      if (lines.length === 1) {
        return { eyebrow: "", mainTitle: lines[0] };
      }
      return {
        eyebrow: lines[0],
        mainTitle: lines.slice(1).join("\n"),
      };
    },
    html() {
      return normalizePageContent(this.type, this.content).html || "";
    },
    hasHtml() {
      const text = String(this.html)
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
      return text.length > 0 || /<img\b/i.test(this.html);
    },
  },
  watch: {
    html: {
      handler() {
        this.imagePackPasses = 0;
        this.queuePack();
      },
      immediate: true,
    },
    title() {
      this.queuePack();
    },
    type() {
      this.queuePack();
    },
  },
  beforeDestroy() {
    if (this.packTimer) clearTimeout(this.packTimer);
    if (this.unwatchImages) this.unwatchImages();
  },
  methods: {
    isMapPageType,
    queuePack({ fromImages = false } = {}) {
      if (fromImages) {
        if (this.imagePackPasses >= 5) return;
        this.imagePackPasses += 1;
      }
      if (this.packQueued) return;
      this.packQueued = true;
      if (this.packTimer) clearTimeout(this.packTimer);
      this.packTimer = setTimeout(() => {
        this.packTimer = null;
        this.packQueued = false;
        this.packPages();
      }, fromImages ? 50 : 0);
    },
    packPages() {
      if (this.unwatchImages) {
        this.unwatchImages();
        this.unwatchImages = null;
      }

      if (!this.hasHtml) {
        this.previewPages = [];
        return;
      }

      this.previewPages = packProgramPageHtml(this.html, this.$refs.measureHost, {
        hasTitle: !isMapPageType(this.type),
        htmlClassName: "program-page-preview__html program-page-preview__measure-inner",
      });

      if (this.imagePackPasses < 5) {
        this.unwatchImages = watchImagesForRepack(this.html, () => this.queuePack({ fromImages: true }));
      }
    },
  },
};
</script>

<style lang="scss">
.program-page-preview__page-wrap {
  margin-bottom: 20px;
}

.program-page-preview__sheet {
  width: 100%;
  max-width: 780px;
  min-height: 980px;
  padding: 40px 44px 36px;
  background: #fff;
  border: 1px solid #cfcfcf;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.program-page-preview__header-placeholder {
  height: 90px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.program-page-preview__logo-mark {
  width: 120px;
  height: 48px;
  border: 1px dashed #d0d0d0;
  border-radius: 4px;
  opacity: 0.7;
}

.program-page-preview__title-block {
  text-align: center;
  margin: 4px 0 24px;
}

.program-page-preview__eyebrow {
  font-family: "Calibri", sans-serif;
  font-size: 11pt;
  font-weight: 400;
  color: #c5a069;
  line-height: 1.4;
  margin: 0 0 8px;
}

.program-page-preview__title {
  font-family: "Calibri", sans-serif;
  font-size: 16pt;
  font-weight: 700;
  margin: 0 auto;
  max-width: 92%;
  color: #c5a069;
  line-height: 1.35;
  white-space: pre-line;
}

.program-page-preview__title-rule {
  width: 86%;
  max-width: 640px;
  height: 1px;
  margin: 16px auto 0;
  background: #111;
}

.program-page-preview__html {
  font-size: 13px;
  line-height: 1.35;
  color: #2a2a2a;

  h1,
  h2,
  h3 {
    margin: 0 0 10px;
    font-weight: 700;
  }

  p {
    margin: 0 0 10px;
  }

  ul,
  ol {
    margin: 0 0 12px;
    padding-left: 20px;
  }

  img {
    display: inline-block;
    vertical-align: top;
    max-width: 100%;
    height: auto;
    margin: 4px 6px 8px 0;
  }

  .rte-columns {
    display: flex;
    gap: 16px;
    width: 100%;
    margin: 12px 0;
    align-items: flex-start;
    box-sizing: border-box;
  }

  .rte-columns--icon > .rte-column:first-child {
    flex: 0 0 72px;
    max-width: 72px;
    min-width: 72px;
  }

  .rte-columns--icon > .rte-column:last-child {
    flex: 1 1 auto;
  }

  .rte-columns--icon > .rte-column:first-child img {
    width: 100%;
    max-width: 64px;
    height: auto;
    margin: 0;
  }

  .rte-column {
    flex: 1 1 0;
    min-width: 0;
    box-sizing: border-box;
  }

  hr {
    display: none;
  }
}

.program-page-preview--map .program-page-preview__html img {
  width: 100%;
  max-height: 780px;
  object-fit: contain;
}

.program-page-preview__empty {
  padding: 24px 0;
}

.program-page-preview__measure {
  position: absolute;
  left: -10000px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
}
</style>
